import {
    DndContext,
    DragEndEvent,
    DragMoveEvent,
    DragStartEvent,
    DragCancelEvent,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import { Node, useReactFlow } from "@xyflow/react";
import React, {
    memo, useCallback, useMemo, useRef, useState,
} from "react";
import { useWorkflow } from "./WorkflowProvider";
import { WorkflowStep } from "../../../__generated__/api";
import { GROUP, ACTION } from "../workflow/nodes";
import { ActionNodeButton } from "../components/nodes/BaseActionNode";

const GROUP_NODE_TYPES = ["WorkflowStepGroup", "WorkflowServiceChildAction"];
const CONTAINER_GROUP_NODE_TYPES = [GROUP.CONDITION, GROUP.SUBACTION];

function getGroupNodes(nodes: Node[]) {
    return nodes.filter(node => GROUP_NODE_TYPES.includes(node.data.__typename as string)
        && !CONTAINER_GROUP_NODE_TYPES.includes(node.type as GROUP));
}

function getClosestGroupNode(nodes: Node[], movingNode: Node): Node | null {
    const groupNodes = getGroupNodes(nodes);

    if (groupNodes.length === 0) {
        return null;
    }

    const movingX = movingNode.position.x;
    const movingY = movingNode.position.y;

    let closestNode: Node | null = null;
    let minDistance = Number.POSITIVE_INFINITY;

    groupNodes.forEach(groupNode => {
        const currentGroupNodeX = groupNode.position.x;
        const currentGroupNodeY = groupNode.position.y;

        if (movingX < currentGroupNodeX || movingY < currentGroupNodeY) {
            return;
        }

        const xDistance = movingX - currentGroupNodeX;
        const yDistance = movingY - currentGroupNodeY;

        const distance = Math.sqrt(xDistance * xDistance + yDistance * yDistance);

        if (distance < minDistance) {
            minDistance = distance;
            closestNode = groupNode;
        }
    });

    return closestNode;
}

const isDescendantOf = (nodeA: Node, ancestorId: string, nodesMap: Map<string, Node>): boolean => {
    let parentId = nodeA.data?.parentId as string | undefined;

    while (parentId) {
        if (parentId === ancestorId) {
            return true;
        }
        const parentNode = nodesMap.get(parentId);
        parentId = parentNode?.data?.parentId as string | undefined;
    }
    return false;
};

function DragAndDropProvider({ children }: { children: React.ReactNode }) {
    const { getNodes, getNode, setCenter } = useReactFlow();
    const {
        updateNodeChildrenInWorkflow,
        addNodeToWorkflow,
        deleteNodeFromWorkflow,
        ghostParentAndIndex,
        setGhostParentAndIndex,
    } = useWorkflow();
    const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
    const [shouldDisableDropAnimation, setShouldDisableDropAnimation] = useState(false);
    const draggingNodeInitialPosition = useRef<{ x: number; y: number } | null>(null);

    const { setNodeRef } = useDroppable({
        id: "droppable",
    });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    const clearGhost = useCallback(() => {
        if (ghostParentAndIndex !== null) {
            setGhostParentAndIndex(null);
        }
    }, [ghostParentAndIndex, setGhostParentAndIndex]);

    const handleDragMove = useCallback((event: DragMoveEvent) => {
        const { delta, active } = event;
        const movingNodeId = event.active.id.toString();

        const movingNode = active.data.current;

        if (!movingNode) {
            return;
        }

        if (!draggingNodeInitialPosition.current) {
            draggingNodeInitialPosition.current = {
                x: movingNode.positionAbsoluteX,
                y: movingNode.positionAbsoluteY,
            };
        }

        const xPosition = draggingNodeInitialPosition.current.x;
        const yPosition = draggingNodeInitialPosition.current.y;

        const tentativeNode = {
            ...movingNode,
            position: {
                x: (xPosition ?? 0) + delta.x,
                y: (yPosition ?? 0) + delta.y,
            },
        } as Node;

        const nodes = getNodes();
        const nodesMap = new Map<string, Node>();
        nodes.forEach(nodeItem => nodesMap.set(nodeItem.id, nodeItem));

        const closestGroupNode = getClosestGroupNode(nodes, tentativeNode);

        if (!closestGroupNode || isDescendantOf(closestGroupNode, movingNodeId, nodesMap)) {
            return;
        }

        const parentGroupId = closestGroupNode.id;

        const childrenOfClosestGroupNode = nodes.filter(
            node => node.data?.parentId === parentGroupId && node.type !== ACTION.GHOST,
        );

        const originalIndex = childrenOfClosestGroupNode.findIndex(node => node.id === movingNodeId);

        let insertIndex = childrenOfClosestGroupNode.findIndex(
            sib => tentativeNode.position.y < sib.position.y,
        );

        if (insertIndex === -1) {
            insertIndex = childrenOfClosestGroupNode.length; // place at end
        }

        const indexDifference = insertIndex - originalIndex;

        // If parent is unchanged and index is unchanged -> no ghost
        if (parentGroupId === tentativeNode.data?.parentId && (indexDifference === 1 || indexDifference === 0)) {
            clearGhost();
            return;
        }

        if (ghostParentAndIndex?.parentId === parentGroupId && ghostParentAndIndex.index === insertIndex) {
            return;
        }

        setGhostParentAndIndex({ parentId: parentGroupId, index: insertIndex });
    }, [getNode, getNodes, ghostParentAndIndex, setGhostParentAndIndex, clearGhost]);

    const handleDragCancel = useCallback((_event: DragCancelEvent) => {
        clearGhost();
        setDraggingNodeId(null);
        draggingNodeInitialPosition.current = null;
    }, [clearGhost]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { delta } = event;
        const movingNodeId = event.active.id.toString();

        clearGhost();
        draggingNodeInitialPosition.current = null;

        const movingNode = getNode(movingNodeId);

        if (!movingNode) {
            return;
        }

        const movingNodeWithUpdatedPosition = {
            ...movingNode,
            position: {
                x: movingNode.position.x + delta.x,
                y: movingNode.position.y + delta.y,
            },
        };

        const nodes = getNodes();

        const nodesMap = new Map<string, Node>();
        nodes.forEach(node => nodesMap.set(node.id, node));

        const closestGroupNode = getClosestGroupNode(nodes, movingNodeWithUpdatedPosition);
        if (!closestGroupNode) {
            return;
        }

        // Disallow if we're trying to drop into a descendant of the moving node
        if (isDescendantOf(closestGroupNode, movingNodeId, nodesMap)) {
            return;
        }

        // check if draggind within same group
        if (movingNodeWithUpdatedPosition.data.parentId === closestGroupNode.id) {
            const nodeChildren = nodes.filter(node => node.data.parentId === closestGroupNode.id).map(node => {
                if (node.id === movingNodeId) {
                    return movingNodeWithUpdatedPosition;
                }
                return node;
            });
            const sortedChildren = nodeChildren.toSorted((nodeA, nodeB) => (nodeA.position.y ?? 0) - (nodeB.position.y ?? 0));
            if (sortedChildren.every((child, index) => child.id === nodeChildren[index]?.id)) {
                return;
            }
            const workflowStepsFromNodes = sortedChildren.map(child => child.data as WorkflowStep);

            updateNodeChildrenInWorkflow(closestGroupNode.id, workflowStepsFromNodes);

            setShouldDisableDropAnimation(true);
            setDraggingNodeId(null);

            setCenter(movingNodeWithUpdatedPosition.position.x, movingNodeWithUpdatedPosition.position.y, {
                duration: 200,
                zoom: 1,
            });
            return;
        }

        const movingNodeWithUpdatedParentId = {
            ...movingNodeWithUpdatedPosition,
            data: {
                ...movingNodeWithUpdatedPosition.data as WorkflowStep,
                parentId: closestGroupNode.id,
            },
        };

        const parentId = closestGroupNode.id;
        deleteNodeFromWorkflow({
            nodeId: movingNodeId,
            shouldMarkDirty: false,
        });
        addNodeToWorkflow({
            newNode: movingNodeWithUpdatedParentId.data as WorkflowStep,
            parentNodeId: parentId,
            shouldMarkDirty: true,
        });

        setShouldDisableDropAnimation(true);
        setDraggingNodeId(null);

        setCenter(movingNodeWithUpdatedParentId.position.x, movingNodeWithUpdatedParentId.position.y, {
            duration: 200,
            zoom: 1,
        });
        clearGhost();
    }, [addNodeToWorkflow, deleteNodeFromWorkflow, getNode, getNodes, updateNodeChildrenInWorkflow, clearGhost]);

    const handleDragStart = useCallback((event: DragStartEvent) => {
        // Reset drop animation to default behaviour when a new drag starts
        setShouldDisableDropAnimation(false);
        setDraggingNodeId(event.active.id.toString());
    }, []);

    const activeNode = useMemo(() => (draggingNodeId ? getNode(draggingNodeId) : null), [draggingNodeId, getNode]);

    return (
        <DndContext
            onDragMove={handleDragMove}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            sensors={sensors}
        >
            <div ref={setNodeRef}>
                {children}
            </div>

            <DragOverlay dropAnimation={shouldDisableDropAnimation ? null : undefined}>
                {draggingNodeId && (
                    <ActionNodeButton
                        shouldDisableSettings
                        action={activeNode?.data as WorkflowStep & { label?: string }}
                    />
                )}
            </DragOverlay>
        </DndContext>
    );
}

export default memo(DragAndDropProvider);
